import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { OurSquareLogo } from '../../components/brand/Logo';
import { 
  FileText, Users, AlertTriangle, Clock, CheckCircle, 
  ChevronRight, Home, Trophy, PenTool, Building, UserPlus,
  Calendar, Bell, TrendingUp, BarChart3
} from 'lucide-react';

export default function StaffDashboardPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    intakeStats: {},
    pendingApplications: [],
    pendingMilestones: [],
    pendingSignatures: [],
    referralStats: {},
    pendingReferrals: [],
    housingStats: {},
    recentIncidents: [],
  });

  useEffect(() => { loadDashboard(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboard = async () => {
    setLoading(false);
    try {
      const [
        intakeStatsRes,
        intakeAppsRes,
        milestonesRes,
        signaturesRes,
        referralAnalyticsRes,
        referralsRes,
        housesRes,
      ] = await Promise.all([
        api.get('/intake/stats?entity=our_square').catch(() => ({ data: {} })),
        api.get('/intake/applications?entity=our_square&status=pending').catch(() => ({ data: [] })),
        api.get('/milestones/pending-approvals?entity=our_square').catch(() => ({ data: [] })),
        api.get('/housing-documents/pending-signatures?entity=our_square').catch(() => ({ data: [] })),
        api.get('/referral-sources/analytics/summary?entity=our_square').catch(() => ({ data: {} })),
        api.get('/housing-referrals?entity=our_square&status=pending').catch(() => ({ data: [] })),
        api.get('/housing/entity/our_square/houses').catch(() => ({ data: [] })),
      ]);

      // Calculate housing stats
      const houses = housesRes.data || [];
      const totalBeds = houses.reduce((sum, h) => sum + (h.bed_count || 0), 0);
      const totalResidents = houses.reduce((sum, h) => sum + (h.residents?.length || 0), 0);
      const occupancyRate = totalBeds > 0 ? Math.round((totalResidents / totalBeds) * 100) : 0;

      setDashboardData({
        intakeStats: intakeStatsRes.data || {},
        pendingApplications: (intakeAppsRes.data || []).slice(0, 5),
        pendingMilestones: (milestonesRes.data || []).slice(0, 5),
        pendingSignatures: (signaturesRes.data || []).slice(0, 5),
        referralStats: referralAnalyticsRes.data || {},
        pendingReferrals: (referralsRes.data || []).slice(0, 5),
        housingStats: { totalBeds, totalResidents, occupancyRate, properties: houses.length },
        recentIncidents: [],
      });
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const { intakeStats, pendingApplications, pendingMilestones, pendingSignatures, referralStats, pendingReferrals, housingStats } = dashboardData;

  // Count total action items
  const totalActions = (pendingApplications?.length || 0) + (pendingMilestones?.length || 0) + (pendingSignatures?.length || 0) + (pendingReferrals?.length || 0);

  return (
    <div className="space-y-6" data-testid="staff-dashboard-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <OurSquareLogo size="sm" className="h-14 w-auto" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Staff Dashboard</h1>
          <p className="text-sm text-muted-foreground">Welcome back, {user?.full_name}</p>
        </div>
      </div>

      {/* Priority Actions Alert */}
      {totalActions > 0 && (
        <Card className="border-0 shadow-sm rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Bell size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-800">{totalActions} Action Items Require Your Attention</p>
                  <p className="text-sm text-amber-600">
                    {pendingApplications?.length || 0} applications • {pendingMilestones?.length || 0} milestones • {pendingSignatures?.length || 0} signatures • {pendingReferrals?.length || 0} referrals
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/our-square/properties')}>
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center mb-2"><Home size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{housingStats.properties || 0}</p>
            <p className="text-xs text-muted-foreground">Properties</p>
            <div className="flex items-center gap-1 mt-2">
              <Progress value={housingStats.occupancyRate || 0} className="h-1.5 flex-1" />
              <span className="text-xs font-medium">{housingStats.occupancyRate || 0}%</span>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/our-square/residents')}>
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center mb-2"><Users size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{housingStats.totalResidents || 0}</p>
            <p className="text-xs text-muted-foreground">Active Residents</p>
            <p className="text-xs text-emerald-600 mt-2">{housingStats.totalBeds || 0} total beds</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/our-square/intake')}>
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center mb-2"><FileText size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{intakeStats.pending || 0}</p>
            <p className="text-xs text-muted-foreground">Pending Applications</p>
            <p className="text-xs text-emerald-600 mt-2">{intakeStats.approved || 0} approved this period</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/our-square/referral-sources')}>
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center mb-2"><Building size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{referralStats.total_sources || 0}</p>
            <p className="text-xs text-muted-foreground">Referral Sources</p>
            <p className="text-xs text-purple-600 mt-2">{referralStats.pending_referrals || 0} pending referrals</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Items Grid */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Pending Applications */}
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText size={16} className="text-amber-600" /> Pending Applications
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/our-square/intake')} className="text-xs">
              View All <ChevronRight size={14} />
            </Button>
          </CardHeader>
          <CardContent>
            {pendingApplications?.length > 0 ? (
              <div className="space-y-2">
                {pendingApplications.map(app => (
                  <div key={app.id} className="flex items-center justify-between p-3 rounded-lg bg-amber-50 hover:bg-amber-100 cursor-pointer transition-colors" onClick={() => navigate('/our-square/intake')}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 text-sm font-bold">
                        {app.applicant_name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{app.applicant_name}</p>
                        <p className="text-xs text-muted-foreground">{new Date(app.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 border border-amber-200">Review</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                <p className="text-sm">No pending applications</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Milestones */}
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy size={16} className="text-purple-600" /> Milestone Approvals
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/our-square/milestones')} className="text-xs">
              View All <ChevronRight size={14} />
            </Button>
          </CardHeader>
          <CardContent>
            {pendingMilestones?.length > 0 ? (
              <div className="space-y-2">
                {pendingMilestones.map(m => (
                  <div key={m.resident_id} className="flex items-center justify-between p-3 rounded-lg bg-purple-50 hover:bg-purple-100 cursor-pointer transition-colors" onClick={() => navigate('/our-square/milestones')}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 text-sm font-bold">
                        {m.resident_name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{m.resident_name}</p>
                        <p className="text-xs text-purple-600">{m.next_milestone?.name}</p>
                      </div>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700 border border-purple-200">{m.completed_count}/{m.total_count}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                <p className="text-sm">All milestones up to date</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Signatures */}
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <PenTool size={16} className="text-cyan-600" /> Documents Awaiting Signature
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/our-square/documents')} className="text-xs">
              View All <ChevronRight size={14} />
            </Button>
          </CardHeader>
          <CardContent>
            {pendingSignatures?.length > 0 ? (
              <div className="space-y-2">
                {pendingSignatures.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg bg-cyan-50 hover:bg-cyan-100 cursor-pointer transition-colors" onClick={() => navigate('/our-square/documents')}>
                    <div className="flex items-center gap-3">
                      <FileText size={18} className="text-cyan-600" />
                      <div>
                        <p className="font-medium text-sm">{doc.resident_name}</p>
                        <p className="text-xs text-cyan-600">{doc.document_type_name}</p>
                      </div>
                    </div>
                    <Badge className="bg-cyan-100 text-cyan-700 border border-cyan-200">Sign</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                <p className="text-sm">All documents signed</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Referrals */}
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <UserPlus size={16} className="text-emerald-600" /> Incoming Referrals
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/our-square/referral-sources')} className="text-xs">
              View All <ChevronRight size={14} />
            </Button>
          </CardHeader>
          <CardContent>
            {pendingReferrals?.length > 0 ? (
              <div className="space-y-2">
                {pendingReferrals.map(ref => (
                  <div key={ref.id} className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 hover:bg-emerald-100 cursor-pointer transition-colors" onClick={() => navigate('/our-square/referral-sources')}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center text-emerald-700 text-sm font-bold">
                        {ref.resident_name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{ref.resident_name}</p>
                        <p className="text-xs text-emerald-600">From: {ref.referral_source_name}</p>
                      </div>
                    </div>
                    <Badge className={ref.priority === 'urgent' ? 'bg-red-100 text-red-700' : ref.priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>{ref.priority}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                <p className="text-sm">No pending referrals</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Button variant="outline" className="h-auto py-4 flex flex-col gap-2 rounded-xl hover:bg-cyan-50 hover:border-cyan-200" onClick={() => navigate('/our-square/intake')}>
              <FileText size={20} className="text-cyan-600" />
              <span className="text-xs">New Application</span>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex flex-col gap-2 rounded-xl hover:bg-purple-50 hover:border-purple-200" onClick={() => navigate('/our-square/milestones')}>
              <Trophy size={20} className="text-purple-600" />
              <span className="text-xs">Approve Milestone</span>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex flex-col gap-2 rounded-xl hover:bg-emerald-50 hover:border-emerald-200" onClick={() => navigate('/our-square/referral-sources')}>
              <Building size={20} className="text-emerald-600" />
              <span className="text-xs">Add Referral Source</span>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex flex-col gap-2 rounded-xl hover:bg-amber-50 hover:border-amber-200" onClick={() => navigate('/our-square/reports')}>
              <BarChart3 size={20} className="text-amber-600" />
              <span className="text-xs">View Reports</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
